import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  List, MagnifyingGlass, Moon, ShoppingBag, Sun, UserCircle, WhatsappLogo, X,
} from '@phosphor-icons/react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { supportedLanguages, useI18n } from '../../i18n';
import LoginModal from '../auth/LoginModal.js';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showLoginModal, setShowLoginModal] = useState(false);
  const searchRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDarkMode, toggleTheme } = useTheme();
  const { user } = useAuth();
  const { getCartCount } = useCart();
  const { language, changeLanguage, t } = useI18n();

  const navigation = [
    ['/', t('common.home')],
    ['/gallery', t('common.gallery')],
    ['/store', t('common.store')],
    ['/about', t('common.about')],
    ['/contact', t('common.contact')],
    ['/blog', t('common.blog')],
  ];

  useEffect(() => {
    setIsMenuOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (searchOpen) searchRef.current?.focus();
  }, [searchOpen]);

  const handleSearch = (event) => {
    event.preventDefault();
    const query = searchQuery.trim();
    if (!query) return;
    navigate(`/gallery?search=${encodeURIComponent(query)}`);
    setSearchQuery('');
    setSearchOpen(false);
  };

  const utilityClass = 'inline-flex h-10 w-10 items-center justify-center rounded-full border border-border text-text-primary transition-colors hover:border-primary hover:text-primary';

  return (
    <header className="fixed inset-x-0 top-0 z-50 h-[var(--header-height)] border-b border-border bg-surface/95 backdrop-blur-xl">
      <div className="page-shell grid h-full grid-cols-[1fr_auto] items-center gap-4 lg:grid-cols-[260px_1fr_300px]">
        <Link to="/" className="flex w-max items-center gap-3 text-text-primary" aria-label="TafsirArt">
          <img src="/favicon.ico" alt="" className="h-8 w-8 object-contain" aria-hidden="true" />
          <span className="font-display text-[1.75rem] font-semibold tracking-[-0.03em]">TAFSIRART</span>
        </Link>

        <nav className="hidden items-center justify-center gap-7 lg:flex" aria-label="Primary navigation">
          {navigation.map(([to, label]) => (
            <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
              {label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center justify-end gap-2">
          <label className="hidden items-center md:flex">
            <span className="sr-only">{t('common.language')}</span>
            <select
              value={language}
              onChange={(event) => changeLanguage(event.target.value)}
              className="h-10 border-0 bg-transparent px-2 text-xs font-semibold tracking-[.08em] text-text-primary focus:outline-none"
            >
              {supportedLanguages.map((item) => <option key={item.code} value={item.code}>{item.short}</option>)}
            </select>
          </label>

          <div className={`hidden items-center overflow-hidden transition-all duration-300 md:flex ${searchOpen ? 'w-52' : 'w-10'}`}>
            {searchOpen ? (
              <form onSubmit={handleSearch} className="flex h-10 w-full items-center border-b border-text-primary">
                <input
                  ref={searchRef}
                  value={searchQuery}
                  onChange={(event) => setSearchQuery(event.target.value)}
                  placeholder={t('common.searchPlaceholder')}
                  className="min-w-0 flex-1 bg-transparent px-2 text-sm text-text-primary outline-none placeholder:text-text-muted"
                />
                <button type="button" onClick={() => setSearchOpen(false)} className="p-2 text-text-secondary" aria-label={t('common.close')}><X size={18} /></button>
              </form>
            ) : (
              <button type="button" onClick={() => setSearchOpen(true)} className={utilityClass} aria-label={t('common.search')}><MagnifyingGlass size={19} /></button>
            )}
          </div>

          <button type="button" onClick={toggleTheme} className={utilityClass} aria-label={isDarkMode ? t('common.lightMode') : t('common.darkMode')}>
            {isDarkMode ? <Sun size={19} /> : <Moon size={19} />}
          </button>

          {user ? (
            <Link to="/account" className={`${utilityClass} hidden sm:inline-flex`} aria-label={t('common.account')}><UserCircle size={20} /></Link>
          ) : (
            <button type="button" onClick={() => setShowLoginModal(true)} className={`${utilityClass} hidden sm:inline-flex`} aria-label={t('common.account')}><UserCircle size={20} /></button>
          )}

          <Link to="/cart" className={`${utilityClass} relative`} aria-label={t('common.cart')}>
            <ShoppingBag size={19} />
            {getCartCount() > 0 && <span className="absolute -right-1 -top-1 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-[10px] font-semibold text-white">{getCartCount()}</span>}
          </Link>

          <button type="button" onClick={() => setIsMenuOpen((open) => !open)} className={`${utilityClass} lg:hidden`} aria-label={isMenuOpen ? t('common.close') : t('common.menu')}>
            {isMenuOpen ? <X size={21} /> : <List size={21} />}
          </button>
        </div>
      </div>

      <div className={`absolute inset-x-0 top-full border-b border-border bg-surface shadow-xl transition-all duration-300 lg:hidden ${isMenuOpen ? 'visible translate-y-0 opacity-100' : 'invisible -translate-y-3 opacity-0'}`}>
        <div className="page-shell py-5">
          <nav className="grid gap-1" aria-label="Mobile navigation">
            {navigation.map(([to, label]) => (
              <NavLink key={to} to={to} end={to === '/'} className={({ isActive }) => `border-b border-border-light px-2 py-3 text-sm font-medium ${isActive ? 'text-primary' : 'text-text-primary'}`}>
                {label}
              </NavLink>
            ))}
          </nav>

          <form onSubmit={handleSearch} className="mt-5 flex gap-2">
            <input value={searchQuery} onChange={(event) => setSearchQuery(event.target.value)} placeholder={t('common.searchPlaceholder')} className="field" />
            <button type="submit" className="btn-primary px-4" aria-label={t('common.search')}><MagnifyingGlass size={19} /></button>
          </form>

          <div className="mt-5 flex items-center justify-between gap-4">
            <div className="flex gap-2" aria-label={t('common.language')}>
              {supportedLanguages.map((item) => (
                <button key={item.code} type="button" onClick={() => changeLanguage(item.code)} className={`h-10 border px-3 text-xs font-semibold ${language === item.code ? 'border-text-primary bg-text-primary text-surface' : 'border-border text-text-secondary'}`}>
                  {item.short}
                </button>
              ))}
            </div>
            <a href="https://wa.me/27641304056" target="_blank" rel="noopener noreferrer" className={utilityClass} aria-label="WhatsApp"><WhatsappLogo size={20} /></a>
          </div>
        </div>
      </div>

      <LoginModal
        isOpen={showLoginModal}
        onClose={(success) => { setShowLoginModal(false); if (success) window.location.reload(); }}
        onSwitchToRegister={() => { setShowLoginModal(false); navigate('/register', { state: { from: { pathname: location.pathname } } }); }}
        returnPath={location.pathname}
      />
    </header>
  );
};

export default Header;
